/**
 * useAlertManagement Hook
 * Phase 1 Day 3 - Device alert state management
 *
 * Manages three types of device alerts:
 * - Forced Entry: Door forced open without valid credential
 * - Propped Door: Door held open beyond timeout
 * - Event Denied: Access denied event
 *
 * All alerts are stored in UIStore for persistence across component remounts.
 *
 * @returns {Object} Alert states and management functions
 */

import { useUIStore } from "../stores";
import logger from "../utils/logger";

export const useAlertManagement = () => {
  // Get alert states from UIStore
  const forcedEntryAlert = useUIStore((state) => state.forcedEntryAlert);
  const setForcedEntryAlert = useUIStore((state) => state.setForcedEntryAlert);
  const clearForcedEntryAlert = useUIStore(
    (state) => state.clearForcedEntryAlert
  );

  const proppedDoorAlert = useUIStore((state) => state.proppedDoorAlert);
  const setProppedDoorAlert = useUIStore((state) => state.setProppedDoorAlert);
  const clearProppedDoorAlert = useUIStore(
    (state) => state.clearProppedDoorAlert
  );

  const eventDeniedAlert = useUIStore((state) => state.eventDeniedAlert);
  const setEventDeniedAlert = useUIStore((state) => state.setEventDeniedAlert);
  const clearEventDeniedAlert = useUIStore(
    (state) => state.clearEventDeniedAlert
  );

  /**
   * Show forced entry alert
   * @param {Object} alertData - Alert data {deviceId, timestamp, ...}
   */
  const showForcedEntryAlert = (alertData) => {
    logger.log(`[useAlertManagement] Showing forced entry alert:`, alertData);
    setForcedEntryAlert(alertData);
  };

  /**
   * Show propped door alert
   * @param {Object} alertData - Alert data {deviceId, timestamp, ...}
   */
  const showProppedDoorAlert = (alertData) => {
    logger.log(`[useAlertManagement] Showing propped door alert:`, alertData);
    setProppedDoorAlert(alertData);
  };

  /**
   * Show event denied alert
   * @param {Object} alertData - Alert data {deviceId, timestamp, ...}
   */
  const showEventDeniedAlert = (alertData) => {
    logger.log(`[useAlertManagement] Showing event denied alert:`, alertData);
    setEventDeniedAlert(alertData);
  };

  /**
   * Close forced entry alert
   */
  const closeForcedEntryAlert = () => {
    logger.log("[useAlertManagement] Closing forced entry alert");
    clearForcedEntryAlert();
  };

  /**
   * Close propped door alert
   */
  const closeProppedDoorAlert = () => {
    logger.log("[useAlertManagement] Closing propped door alert");
    clearProppedDoorAlert();
  };

  /**
   * Close event denied alert
   */
  const closeEventDeniedAlert = () => {
    logger.log("[useAlertManagement] Closing event denied alert");
    clearEventDeniedAlert();
  };

  /**
   * Clear all alerts
   */
  const clearAllAlerts = () => {
    logger.log("[useAlertManagement] Clearing all alerts");
    clearForcedEntryAlert();
    clearProppedDoorAlert();
    clearEventDeniedAlert();
  };

  /**
   * Check if any alerts are active
   * @returns {boolean} True if any alert is active
   */
  const hasActiveAlerts = () => {
    return Boolean(forcedEntryAlert || proppedDoorAlert || eventDeniedAlert);
  };

  return {
    // Forced Entry Alert
    forcedEntryAlert,
    setForcedEntryAlert, // Backward compatibility
    clearForcedEntryAlert, // Backward compatibility
    showForcedEntryAlert,
    closeForcedEntryAlert,

    // Propped Door Alert
    proppedDoorAlert,
    setProppedDoorAlert, // Backward compatibility
    clearProppedDoorAlert, // Backward compatibility
    showProppedDoorAlert,
    closeProppedDoorAlert,

    // Event Denied Alert
    eventDeniedAlert,
    setEventDeniedAlert, // Backward compatibility
    clearEventDeniedAlert, // Backward compatibility
    showEventDeniedAlert,
    closeEventDeniedAlert,

    // Utility functions
    clearAllAlerts,
    hasActiveAlerts,
  };
};
